/**
 * persistCollectionGroup
 *
 * Read-only Firebase subscription for a Firestore collection group.
 * Loads data via onSnapshot. No local writes — group queries span
 * many parent paths and have no single write target.
 */

import {
  collectionGroup,
  endAt as firestoreEndAt,
  endBefore as firestoreEndBefore,
  limit as firestoreLimit,
  onSnapshot,
  orderBy as firestoreOrderBy,
  query,
  startAfter as firestoreStartAfter,
  startAt as firestoreStartAt,
  where as firestoreWhere,
  type Query,
  type QueryConstraint,
  type QuerySnapshot,
} from 'firebase/firestore';
import { firestore } from '@/services/startFirestore';
import type { CollectionState, QueryFilter, QueryOptions } from '../Store.types';
import { mergeDocumentIntoProxy } from '../core/mergeDocumentIntoProxy';

const buildConstraintsFromFilters = (filters: QueryFilter[]): QueryConstraint[] => {
  return filters.map((f) => {
    switch (f.type) {
      case 'where': return firestoreWhere(f.field, f.op, f.value);
      case 'orderBy': return firestoreOrderBy(f.field, f.direction);
      case 'limit': return firestoreLimit(f.n);
      case 'startAt': return Array.isArray(f.cursor) ? firestoreStartAt(...f.cursor) : firestoreStartAt(f.cursor);
      case 'startAfter': return Array.isArray(f.cursor) ? firestoreStartAfter(...f.cursor) : firestoreStartAfter(f.cursor);
      case 'endAt': return Array.isArray(f.cursor) ? firestoreEndAt(...f.cursor) : firestoreEndAt(f.cursor);
      case 'endBefore': return Array.isArray(f.cursor) ? firestoreEndBefore(...f.cursor) : firestoreEndBefore(f.cursor);
    }
  });
};

export type CollectionGroupPersist = {
  persistType: 'collectionGroup';
  query: Query;
  getLastQuerySnapshot: () => QuerySnapshot | null;
};

type PersistCollectionGroupResult = {
  persist: CollectionGroupPersist;
  unsubscribe: () => void;
};

export function persistCollectionGroup<T extends Record<string, unknown>>(
  collectionId: string,
  state: CollectionState<T>,
  onError?: (error: Error) => void,
  queryOptions?: QueryOptions,
  isActive?: () => boolean,
): PersistCollectionGroupResult {
  const groupRef = collectionGroup(firestore, collectionId);

  const orderByConstraint = queryOptions?.orderBy
    ? [firestoreOrderBy(queryOptions.orderBy.field, queryOptions.orderBy.direction)]
    : [];
  const extraConstraints = buildConstraintsFromFilters(queryOptions?.extraConstraints ?? []);

  const q = query(groupRef, ...orderByConstraint, ...extraConstraints);

  let lastQuerySnapshot: QuerySnapshot | null = null;

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      if (isActive && !isActive()) return;
      lastQuerySnapshot = snapshot;

      snapshot.docChanges().forEach((change) => {
        const docPath = change.doc.ref.path;

        if (change.type === 'removed') {
          state.docs.delete(docPath);
          if (change.oldIndex >= 0 && state.docIds[change.oldIndex] === docPath) {
            state.docIds.splice(change.oldIndex, 1);
          } else {
            const idx = state.docIds.indexOf(docPath);
            if (idx !== -1) state.docIds.splice(idx, 1);
          }
          return;
        }

        if (change.type === 'added') {
          if (change.newIndex >= 0) {
            const existingIdx = state.docIds.indexOf(docPath);
            if (existingIdx !== -1) state.docIds.splice(existingIdx, 1);
            state.docIds.splice(change.newIndex, 0, docPath);
          } else if (!state.docIds.includes(docPath)) {
            state.docIds.push(docPath);
          }
        } else if (change.type === 'modified') {
          if (change.oldIndex !== change.newIndex && change.newIndex >= 0) {
            if (change.oldIndex >= 0 && state.docIds[change.oldIndex] === docPath) {
              state.docIds.splice(change.oldIndex, 1);
            } else {
              const idx = state.docIds.indexOf(docPath);
              if (idx !== -1) state.docIds.splice(idx, 1);
            }
            state.docIds.splice(change.newIndex, 0, docPath);
          }
        }

        const data = change.doc.data() as T;
        const existing = state.docs.get(docPath);
        if (existing) {
          mergeDocumentIntoProxy(
            existing.data as Record<string, unknown>,
            data as Record<string, unknown>,
            change.doc.id,
          );
        } else {
          state.docs.set(docPath, { data: { ...data, docId: change.doc.id } });
        }
      });

      state.loading = false;
    },
    (error) => {
      if (isActive && !isActive()) return;
      state.loading = false;
      onError?.(error);
    },
  );

  return {
    persist: {
      persistType: 'collectionGroup',
      query: q,
      getLastQuerySnapshot: () => lastQuerySnapshot,
    },
    unsubscribe,
  };
}
